/**
 * Budget planner math. Entered channel rows (TV, CTV, Print...) and the spend the ad
 * accounts report (Google Ads, LSA, Meta) are merged by channel; an entered figure always
 * wins, so nothing is counted twice. Unknown history never becomes zero.
 */
export type BudgetValues = { planned_budget?: number | null; actual_spend?: number | null; leads?: number | null; revenue?: number | null; notes?: string };
export type BudgetEntry = BudgetValues & { id: number; month: string; channel: string };
export type SyncedSpend = { month: string; platform: string; channel: string; actual_spend: number | null; leads: number | null };
export type BudgetRow = {
  key: string;
  channel: string;
  entry: BudgetEntry | null;
  planned: number | null;
  actual: number | null;
  leads: number | null;
  revenue: number | null;
  /** Spend or leads came from the ad accounts, not typed in. */
  fromAccounts: boolean;
};
export type ChannelRate = { months: number; spend: number; leads: number | null; costPerLead: number | null; revenuePerDollar: number | null };

const ALIASES: Record<string, string> = {
  googleads: "google ads", google: "google ads", adwords: "google ads", googlesearch: "google ads", googleppc: "google ads", ppc: "google ads",
  lsa: "local services ads", localservicesads: "local services ads", googlelocalservices: "local services ads", googlelsa: "local services ads", localservices: "local services ads",
  meta: "meta", metaads: "meta", facebook: "meta", facebookads: "meta", instagram: "meta", fb: "meta", facebookandinstagram: "meta",
  microsoftads: "microsoft ads", bing: "microsoft ads", bingads: "microsoft ads",
  youtube: "youtube", youtubeads: "youtube",
};
/** One key per channel, so "Google Ads", "google_ads" and "Google" are the same row. */
export function channelKey(name: string) {
  const flat = name.toLowerCase().replace(/[^a-z0-9]+/g, "");
  return ALIASES[flat] ?? name.trim().toLowerCase().replace(/\s+/g, " ");
}

const add = (a: number | null, b: number | null | undefined) => (b == null ? a : (a ?? 0) + b);

/** One month's rows: every entered channel plus every ad account with spend. */
export function mergeMonth(entries: BudgetEntry[], synced: SyncedSpend[]): BudgetRow[] {
  const rows = new Map<string, BudgetRow>();
  for (const e of entries) {
    rows.set(channelKey(e.channel), {
      key: channelKey(e.channel), channel: e.channel, entry: e,
      planned: e.planned_budget ?? null, actual: e.actual_spend ?? null, leads: e.leads ?? null, revenue: e.revenue ?? null, fromAccounts: false,
    });
  }
  // Several platforms can land on one channel (YouTube is its own row; Meta only from the Ad spend page).
  const accounts = new Map<string, { channel: string; spend: number | null; leads: number | null }>();
  for (const s of synced) {
    const key = channelKey(s.channel);
    const prior = accounts.get(key) ?? { channel: s.channel, spend: null, leads: null };
    accounts.set(key, { channel: prior.channel, spend: add(prior.spend, s.actual_spend), leads: add(prior.leads, s.leads) });
  }
  for (const [key, a] of accounts) {
    const row = rows.get(key);
    if (!row) {
      rows.set(key, { key, channel: a.channel, entry: null, planned: null, actual: a.spend, leads: a.leads, revenue: null, fromAccounts: true });
      continue;
    }
    if (row.actual == null && a.spend != null) { row.actual = a.spend; row.fromAccounts = true; }
    if (row.leads == null && a.leads != null) { row.leads = a.leads; row.fromAccounts = true; }
  }
  return [...rows.values()].sort((a, b) => (b.planned ?? b.actual ?? 0) - (a.planned ?? a.actual ?? 0) || a.channel.localeCompare(b.channel));
}

/** Each channel's results over its last `window` months with spend, up to and including `month`. */
export function channelRates(history: { months: string[]; entries: BudgetEntry[]; synced: SyncedSpend[] }, month: string, window = 3) {
  const perMonth = history.months.filter((m) => m <= month).map((m) => mergeMonth(history.entries.filter((e) => e.month === m), history.synced.filter((s) => s.month === m)));
  type Sum = { channel: string; months: number; spend: number; leads: number | null; revenue: number | null; revenueSpend: number };
  const sums = new Map<string, Sum>();
  for (const rows of perMonth.reverse()) {
    for (const r of rows) {
      if (r.actual == null || r.actual <= 0) continue;
      const s = sums.get(r.key) ?? { channel: r.channel, months: 0, spend: 0, leads: null, revenue: null, revenueSpend: 0 };
      if (s.months >= window) continue;
      s.months += 1;
      s.spend += r.actual;
      if (r.leads != null) s.leads = (s.leads ?? 0) + r.leads;
      if (r.revenue != null) { s.revenue = (s.revenue ?? 0) + r.revenue; s.revenueSpend += r.actual; }
      sums.set(r.key, s);
    }
  }
  const rates = new Map<string, ChannelRate & { channel: string }>();
  for (const [key, s] of sums) rates.set(key, {
    channel: s.channel, months: s.months, spend: s.spend, leads: s.leads,
    costPerLead: s.leads && s.leads > 0 ? s.spend / s.leads : null,
    revenuePerDollar: s.revenue != null && s.revenueSpend > 0 ? s.revenue / s.revenueSpend : null,
  });
  return rates;
}

/** What a planned amount would bring at the channel's recorded rates. Null when there is no history to go on. */
export function project(planned: number | null | undefined, rate: ChannelRate | undefined) {
  if (planned == null || !Number.isFinite(planned) || planned < 0 || !rate) return { leads: null, revenue: null };
  return {
    leads: rate.costPerLead ? planned / rate.costPerLead : null,
    revenue: rate.revenuePerDollar != null ? planned * rate.revenuePerDollar : null,
  };
}

/** A slider range that fits the channel: room to double it, in round steps. */
export function sliderRange(...values: Array<number | null | undefined>) {
  const top = Math.max(0, ...values.map((v) => (v != null && Number.isFinite(v) ? v : 0)));
  const raw = Math.max(1000, top * 2);
  const step = raw <= 5000 ? 50 : raw <= 20000 ? 100 : raw <= 100000 ? 500 : 1000;
  return { max: Math.ceil(raw / step) * step, step };
}

/** The previous calendar month of "YYYY-MM-01". */
export function previousMonth(month: string) {
  const [y, m] = month.split("-").map(Number);
  return m === 1 ? `${y - 1}-12-01` : `${y}-${String(m - 1).padStart(2, "0")}-01`;
}
